import { defineStore } from 'pinia';
import { uniqBy } from 'lodash-es';
import { LocalStorage } from 'quasar';
import { apiRequestHandler } from '@/utils/api';
import {
  BulkInviteUsers,
  CreateApiKey,
  CreateTeam,
  FindTeamsQueryParams,
  OrganizationMembership,
  Team,
  UpdateOrganizationMembership,
  UpdateTeam,
  UpdateUser,
} from '@/models/user.models';
import { UserModule } from '@/modules/user.module';
import { useAuthStore } from './authStore';
import { useOrganizationStore } from './organizationStore';
import { PaginatedQueryBase } from '@/models/common.models';
import { UploadService } from '@/modules/upload.service';
import {
  ActivityService,
  AssetsService,
  FindNotificationsData,
  UpdateNotificationData,
} from '@/api';
import { notificationFiltersToQueryParams } from './helpers';
import { initTeamChannels } from './events';
import { LocalStorageKeys } from '@/utils/local-storage';

const userModule = new UserModule();

export interface UsersStateInterface {
  usersLoaded: boolean;
  orgMembershipCache: Array<OrganizationMembership>;
  teamCache: Array<Team>;
  unreadNotificationsCount: number;
  usersInvitationResent: Array<string>;
  createApiKeysVisible: boolean;
  pusherStatus: {
    isLoading: boolean;
    connection: boolean;
    orgChannel: boolean;
    userChannel: boolean;
    teamChannels: boolean;
  };
}

export const useUserStore = defineStore('user', {
  /****
   STATE
   ****/
  state: (): UsersStateInterface => {
    return {
      usersLoaded: false,
      orgMembershipCache: [],
      teamCache: [],
      unreadNotificationsCount: 0,
      usersInvitationResent: [],
      createApiKeysVisible: false,
      pusherStatus: {
        isLoading: true,
        connection: false,
        orgChannel: false,
        userChannel: false,
        teamChannels: false,
      },
    };
  },

  /****
   GETTERS
   ****/
  getters: {
    sortedTeamCache(state) {
      if (!state.teamCache) return [];
      return state.teamCache.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    },
  },

  /****
   ACTIONS
   ****/
  actions: {
    setPusherStatus(status: Partial<UsersStateInterface['pusherStatus']>) {
      this.pusherStatus = {
        ...this.pusherStatus,
        ...status,
      };
    },
    clearMemberCache() {
      this.orgMembershipCache = [];
    },
    addMembersToCache(members: Array<OrganizationMembership>) {
      const allMemberships = uniqBy(
        // New memberships should be added to the beginning of the array so that uniqBy pulls the latest one
        [...members, ...this.orgMembershipCache],
        'userId'
      );
      // Also make sure they are unique by user email
      this.orgMembershipCache = uniqBy(allMemberships, 'user.email').sort(
        (a, b) => {
          if (a.user.name < b.user.name) {
            return -1;
          }
          if (a.user.name > b.user.name) {
            return 1;
          }
          return 0;
        }
      );
    },
    setApiKeysVisible(visible: boolean) {
      this.createApiKeysVisible = visible;
    },
    setUsersInvitationResent(userId: string) {
      this.usersInvitationResent.push(userId);
    },
    addTeamsToCache(teams: Array<Team>) {
      for (const team of teams) {
        const existingTeam = this.teamCache.find((t) => t.id === team.id);
        if (existingTeam) {
          Object.assign(existingTeam, team);
        } else {
          this.teamCache.push(team);
        }
      }
    },
    clearTeamCache() {
      this.teamCache = [];
    },

    async updateCurrentUser(data: UpdateUser) {
      const user = await userModule.updateMe(data);
      const authStore = useAuthStore();

      // Get the fresh membership for the current org
      const freshMembership = await this.getOrganizationMembershipById(user.id);

      // Build updated user data for the membership
      const updatedMembershipUser = {
        id: user.id,
        avatarUrl: user.avatarUrl,
        username: user.username,
        name: user.name,
        email: user.email,
        status: user.status,
        timezone: user.timezone,
        mfaMethod: user.mfaMethod,
        passwordLastUpdated: user.passwordLastUpdated,
        phoneNumber: user.phoneNumber || '',
      };

      // Update the fresh membership with the updated user data
      if (freshMembership) {
        freshMembership.user = updatedMembershipUser;
      }

      // Preserve existing memberships from other orgs, update current org's membership
      const existingMemberships = authStore.user?.organizationMemberships || [];
      const updatedMemberships = existingMemberships.map((m) => {
        if (
          freshMembership &&
          m.organizationId === freshMembership.organizationId
        ) {
          return freshMembership;
        }
        // Update user data in other memberships too (same user, different orgs)
        m.user = updatedMembershipUser;
        return m;
      });

      // If membership wasn't in the list, add it
      if (
        freshMembership &&
        !updatedMemberships.find(
          (m) => m.organizationId === freshMembership.organizationId
        )
      ) {
        updatedMemberships.push(freshMembership);
      }

      const updatedUser = {
        ...user,
        organizationMemberships: updatedMemberships,
      };

      authStore.setUser(updatedUser);
      LocalStorage.set(LocalStorageKeys.USER, updatedUser);

      return user;
    },

    async resendEmailVerification() {
      return await userModule.resendMyEmailVerificationCode();
    },

    async verifyEmailCode(code: string) {
      const user = await userModule.verifyMyEmail({ code: code });
      const authStore = useAuthStore();
      authStore.setUser(user);
      return user;
    },

    async getOrganizationMembershipById(id: string) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const membership = await userModule.getOrganizationMembershipById(
        organizationId,
        id
      );
      this.addMembersToCache([membership]);
      return membership;
    },

    async getOrganizationMemberships() {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const memberships = await userModule.getOrganizationMemberships(
        organizationId
      );
      this.clearMemberCache();
      this.addMembersToCache(memberships.data);
      return memberships;
    },

    async uploadFile(file: File) {
      const uploadService = new UploadService();
      const response = await AssetsService.getUserUploadUrl({
        query: {
          fileName: file.name,
          fileType: file.type,
        },
      });
      if (!response.data) {
        throw new Error('Failed to get upload URL');
      }
      return uploadService.uploadFile(
        response.data.url,
        response.data.fields,
        file
      );
    },

    async submitJoinRequest(organizationId: string) {
      return apiRequestHandler(async () => {
        return await userModule.submitJoinRequest(organizationId);
      });
    },

    async approveJoinRequest({
      userId,
      organizationId,
    }: {
      userId: string;
      organizationId: string;
    }) {
      return apiRequestHandler(async () => {
        return await userModule.approveJoinRequest(organizationId, userId);
      });
    },

    async declineJoinRequest({
      userId,
      organizationId,
    }: {
      userId: string;
      organizationId: string;
    }) {
      return apiRequestHandler(async () => {
        return await userModule.declineJoinRequest(organizationId, userId);
      });
    },

    async approveInviteRequest({ organizationId }: { organizationId: string }) {
      return apiRequestHandler(async () => {
        await userModule.approveInviteRequest(organizationId);
        const authStore = useAuthStore();
        return await authStore.refreshToken();
      });
    },

    async declineInviteRequest({ organizationId }: { organizationId: string }) {
      return apiRequestHandler(async () => {
        await userModule.declineInviteRequest(organizationId);
        const authStore = useAuthStore();
        return await authStore.refreshToken();
      });
    },

    async bulkInviteUsers(users: BulkInviteUsers) {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;
        return await userModule.bulkInviteUsers(organizationId, users);
      });
    },

    async sendInvitationEmail(userId: string) {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;
        return await userModule.sendInvitationEmail(organizationId, userId);
      });
    },

    async updateOrganizationMembership({
      userId,
      membership,
      revertCallback,
    }: {
      userId: string;
      membership: UpdateOrganizationMembership;
      revertCallback?: () => void;
    }) {
      return apiRequestHandler(
        async () => {
          const organizationStore = useOrganizationStore();
          const organizationId = organizationStore.activeOrganization?.id;
          if (!organizationId) return;
          const membershipResponse =
            await userModule.updateOrganizationMembership(
              organizationId,
              userId,
              membership
            );
          this.addMembersToCache([membershipResponse]);
          await this.getAllTeams();
          return membershipResponse as OrganizationMembership;
        },
        {
          errorCallback: revertCallback,
        }
      );
    },

    async deleteOrganizationMembership({
      userId,
      orgId,
    }: {
      userId: string;
      orgId?: string;
    }) {
      let orgIdToUse = orgId;
      if (!orgIdToUse) {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;
        orgIdToUse = organizationId;
      }

      const updatedMembership = await userModule.deleteOrganizationMembership(
        orgIdToUse,
        userId
      );
      this.addMembersToCache([updatedMembership]);
      await this.getAllTeams();
    },

    async createTeam(data: CreateTeam) {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;
        const team = await userModule.createTeam(organizationId, data);
        this.addTeamsToCache([team]);
        this.getOrganizationMemberships(); // Refresh memberships

        // Initialize team channel for the newly created team
        const authStore = useAuthStore();
        authStore.onPusherInitialized(() => {
          initTeamChannels(organizationId, [team.id]);
        });

        return team;
      });
    },

    async getAllTeams() {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;
        const teams = await userModule.findTeams(organizationId, {});
        this.clearTeamCache();
        this.addTeamsToCache(teams.data);
        return teams;
      });
    },

    async findTeams(query: FindTeamsQueryParams) {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;
        const teams = await userModule.findTeams(organizationId, query);
        this.addTeamsToCache(teams.data);
        return teams;
      });
    },

    async getTeamById(teamId: string) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      let team: Team;
      try {
        team = await userModule.getTeamById(organizationId, teamId);
        this.addTeamsToCache([team]);
        return team;
      } catch (e) {
        const error = e as { response?: { status?: number } };
        if (error.response?.status === 404) {
          return 'private';
        }
        throw e;
      }
    },

    async updateTeam({
      teamId,
      data,
      revertCallback,
    }: {
      teamId: string;
      data: UpdateTeam;
      revertCallback?: () => void;
    }) {
      return apiRequestHandler(
        async () => {
          const organizationStore = useOrganizationStore();
          const organizationId = organizationStore.activeOrganization?.id;
          if (!organizationId) return;
          const team = await userModule.updateTeam(
            organizationId,
            teamId,
            data
          );
          this.addTeamsToCache([team]);
          this.getOrganizationMemberships(); // Refresh memberships

          // Re-initialize team channel in case team settings changed
          const authStore = useAuthStore();
          authStore.onPusherInitialized(() => {
            initTeamChannels(organizationId, [teamId]);
          });

          return team;
        },
        {
          errorCallback: revertCallback,
        }
      );
    },

    async unarchiveTeam({ teamId }: { teamId: string }) {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;
        const originalTeam = this.teamCache.find((t) => t.id === teamId);
        try {
          if (originalTeam) {
            originalTeam.archived = false;
            this.addTeamsToCache([originalTeam]);
          }
          return await userModule.unarchiveTeam(organizationId, teamId);
        } catch (error) {
          if (originalTeam) {
            originalTeam.archived = true;
            this.addTeamsToCache([originalTeam]);
          }
          throw error;
        }
      });
    },

    async deleteTeam({ teamId }: { teamId: string }) {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;
        const originalTeam = this.teamCache.find((t) => t.id === teamId);
        try {
          if (originalTeam) {
            originalTeam.archived = true;
            this.addTeamsToCache([originalTeam]);
          }
          return await userModule.deleteTeam(organizationId, teamId);
        } catch (error) {
          if (originalTeam) {
            originalTeam.archived = false;
            this.addTeamsToCache([originalTeam]);
          }
          throw error;
        }
      });
    },

    setUnreadNotificationsCount(val: number) {
      this.unreadNotificationsCount = val;
    },

    async findNotifications(query: FindNotificationsData['query']) {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;

        const notifications = await ActivityService.findNotifications<true>({
          path: { organizationId },
          query: notificationFiltersToQueryParams(query),
        });
        return notifications.data;
      });
    },

    async checkForUnreadNotifications() {
      const payload: FindNotificationsData['query'] = {
        limit: 100,
        read: false,
        cursor: '',
      };
      const unreadNotificationResponse = await this.findNotifications(payload);
      if (unreadNotificationResponse?.data?.length) {
        this.setUnreadNotificationsCount(
          unreadNotificationResponse.data.length
        );
      } else {
        this.setUnreadNotificationsCount(0);
      }
    },

    async updateNotification({
      notificationId,
      data,
    }: {
      notificationId: string;
      data: UpdateNotificationData['body'];
    }) {
      return apiRequestHandler(async () => {
        const organizationStore = useOrganizationStore();
        const organizationId = organizationStore.activeOrganization?.id;
        if (!organizationId) return;

        const notification = await ActivityService.updateNotification<true>({
          path: { organizationId, notificationId },
          body: data,
        });
        return notification.data;
      });
    },
    async findUserApiKeys(query: PaginatedQueryBase) {
      return apiRequestHandler(async () => {
        return await userModule.findUserApiKeys(query);
      });
    },
    async createApiKey(organization: { id: string }, data: CreateApiKey) {
      return apiRequestHandler(async () => {
        return await userModule.createApiKey(organization.id, data);
      });
    },
    async deleteApiKey(organizationId: string, apiKey: string) {
      return apiRequestHandler(async () => {
        return await userModule.deleteApiKey(organizationId, apiKey);
      });
    },
    async updateApiKey(
      organizationId: string,
      apiKey: string,
      data: CreateApiKey
    ) {
      return apiRequestHandler(async () => {
        return await userModule.updateApiKey(organizationId, apiKey, data);
      });
    },
  },
});
